import { create } from 'zustand';

// Letter drafts + a history of saved letters. Reusable personal data
// (senders, cities, recipients, signatures) lives in accountStore.
const KEY = 'pdf-tools.letters.v2';

const EMPTY_LETTER = {
  title: '',
  sender: '',
  recipient: '',
  city: '',
  subject: '',
  body: '',
  // attachments: array of { id, label, file? } — file is a File kept
  // in memory only (never persisted).
  attachments: [],
  signature: null, // { dataUrl, w, h }
};

function load() {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

// Attachment File objects can't be serialised — keep labels only.
function stripAttachments(attachments) {
  return (attachments || []).map((a) => ({ id: a.id, label: a.label }));
}

// Persist the whole letters state to localStorage: the history AND the
// in-progress draft, so a reload never loses the current letter.
function persist(s) {
  try {
    const draft = { ...s.draft, attachments: stripAttachments(s.draft.attachments) };
    localStorage.setItem(
      KEY,
      JSON.stringify({ templates: s.templates, draft, editingId: s.editingId })
    );
  } catch {}
}

const stored = load();

// Restore a saved draft (attachment files were dropped on save).
const initialDraft = stored?.draft
  ? {
      ...EMPTY_LETTER,
      ...stored.draft,
      attachments: (stored.draft.attachments || []).map((a) => ({
        id: a.id || crypto.randomUUID(),
        label: a.label || '',
        file: null,
      })),
    }
  : { ...EMPTY_LETTER };

export const useLetters = create((set, get) => ({
  draft: initialDraft,
  // History of saved letters (most recent first).
  templates: stored?.templates || [],
  // Id of the history entry the current draft is bound to (null = new).
  editingId: stored?.editingId || null,

  setField: (key, value) =>
    set((s) => {
      const next = { draft: { ...s.draft, [key]: value } };
      persist({ ...s, ...next });
      return next;
    }),

  setSignature: (sig) =>
    set((s) => {
      const next = { draft: { ...s.draft, signature: sig } };
      persist({ ...s, ...next });
      return next;
    }),

  // Start a fresh letter, optionally pre-filled with account defaults.
  resetDraft: (defaults = {}) =>
    set((s) => {
      const next = { draft: { ...EMPTY_LETTER, ...defaults }, editingId: null };
      persist({ ...s, ...next });
      return next;
    }),

  // Upsert the current draft into history. When the draft is bound to
  // an entry (editingId) that entry is updated in place; otherwise a
  // new entry is created. Returns the entry id.
  commitToHistory: (name) => {
    let id;
    set((s) => {
      const attachments = stripAttachments(s.draft.attachments);
      let next;
      if (s.editingId && s.templates.some((t) => t.id === s.editingId)) {
        id = s.editingId;
        const templates = s.templates.map((t) =>
          t.id === id
            ? { ...t, ...s.draft, attachments, name: name ?? t.name, date: Date.now() }
            : t
        );
        next = { templates };
      } else {
        id = crypto.randomUUID();
        const tpl = {
          id,
          name:
            name ||
            s.draft.title ||
            s.draft.subject ||
            `Lettre ${s.templates.length + 1}`,
          date: Date.now(),
          ...s.draft,
          attachments,
        };
        next = { templates: [tpl, ...s.templates], editingId: id };
      }
      persist({ ...s, ...next });
      return next;
    });
    return id;
  },

  // Load a history entry into the draft for editing.
  loadTemplate: (id) =>
    set((s) => {
      const tpl = s.templates.find((t) => t.id === id);
      if (!tpl) return {};
      const { id: _id, name: _n, date: _d, ...fields } = tpl;
      const attachments = (fields.attachments || []).map((a) => ({
        id: a.id || crypto.randomUUID(),
        label: a.label || '',
        file: null,
      }));
      const next = { draft: { ...EMPTY_LETTER, ...fields, attachments }, editingId: id };
      persist({ ...s, ...next });
      return next;
    }),

  renameTemplate: (id, name) =>
    set((s) => {
      const templates = s.templates.map((t) => (t.id === id ? { ...t, name } : t));
      persist({ ...s, templates });
      return { templates };
    }),

  deleteTemplate: (id) =>
    set((s) => {
      const templates = s.templates.filter((t) => t.id !== id);
      const next = {
        templates,
        editingId: s.editingId === id ? null : s.editingId,
      };
      persist({ ...s, ...next });
      return next;
    }),
}));
